import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import type {
  MeetingShareRecipient,
  MeetingShareRow,
  ShareableUser,
} from '@ai-meeting/shared'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import { logActivity } from '@/services/activity'
import {
  createMeetingShares,
  fetchMeetingShares,
  fetchShareableUsers,
  revokeMeetingShare,
} from '@/services/meetings'

type Step = 'pick' | 'confirm' | 'success' | 'revokeConfirm'

interface ShareMeetingFlowProps {
  meetingId: string
  meetingTitle: string
  onClose: () => void
  /** Called after a successful share so the parent can refresh list counts. */
  onShared?: (
    shared: MeetingShareRecipient[],
    meta?: { newDirectShares: number },
  ) => void
  /** Called after a successful revoke so the parent can refresh counts. */
  onRevoked?: () => void
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

function formatSuccessNames(
  recipients: MeetingShareRecipient[],
  othersTemplate: string,
): string {
  if (recipients.length === 0) return ''
  if (recipients.length === 1) return recipients[0].name
  if (recipients.length === 2) {
    return `${recipients[0].name}, ${recipients[1].name}`
  }
  const shown = recipients.slice(0, 2).map((r) => r.name).join(', ')
  return fmt(othersTemplate, {
    names: shown,
    count: recipients.length - 2,
  })
}

/**
 * Share meeting wizard (BRAI-417): manage current/removed → pick → confirm → success.
 * Uses shareable-users (not tenant directory) and supports revoke with cascade warning.
 */
export default function ShareMeetingFlow({
  meetingId,
  meetingTitle,
  onClose,
  onShared,
  onRevoked,
}: ShareMeetingFlowProps) {
  const { t, locale } = useUiStrings()
  const titleId = useId()
  const searchId = useId()

  const [step, setStep] = useState<Step>('pick')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [users, setUsers] = useState<ShareableUser[]>([])
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [usersError, setUsersError] = useState<string | null>(null)
  const [currentShares, setCurrentShares] = useState<MeetingShareRow[]>([])
  const [removedShares, setRemovedShares] = useState<MeetingShareRow[]>([])
  const [loadingShares, setLoadingShares] = useState(true)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sharedRecipients, setSharedRecipients] = useState<MeetingShareRecipient[]>([])
  const [revokeTarget, setRevokeTarget] = useState<MeetingShareRow | null>(null)
  const [revoking, setRevoking] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search), 300)
    return () => window.clearTimeout(timer)
  }, [search])

  const reloadShares = useCallback(async () => {
    setLoadingShares(true)
    try {
      const data = await fetchMeetingShares(meetingId)
      setCurrentShares(data.current)
      setRemovedShares(data.removed)
    } catch {
      // Non-fatal: picker still works without manage sections.
      setCurrentShares([])
      setRemovedShares([])
    } finally {
      setLoadingShares(false)
    }
  }, [meetingId])

  useEffect(() => {
    void reloadShares()
  }, [reloadShares])

  useEffect(() => {
    let ignore = false
    setLoadingUsers(true)
    setUsersError(null)
    void fetchShareableUsers(meetingId, debouncedSearch)
      .then((result) => {
        if (!ignore) setUsers(result.users)
      })
      .catch((err) => {
        if (!ignore) {
          setUsers([])
          setUsersError(
            err instanceof Error ? err.message : t.failedShareMeetingService,
          )
        }
      })
      .finally(() => {
        if (!ignore) setLoadingUsers(false)
      })
    return () => {
      ignore = true
    }
  }, [meetingId, debouncedSearch, t.failedShareMeetingService])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !submitting && !revoking) {
        if (step === 'revokeConfirm') {
          setRevokeTarget(null)
          setStep('pick')
          return
        }
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose, submitting, revoking, step])

  const selectedUsers = useMemo(
    () => users.filter((u) => selectedIds.includes(u.id)),
    [users, selectedIds],
  )

  function toggleUser(user: ShareableUser) {
    setSelectedIds((prev) =>
      prev.includes(user.id)
        ? prev.filter((id) => id !== user.id)
        : [...prev, user.id],
    )
  }

  async function handleConfirmShare() {
    setSubmitting(true)
    setError(null)
    try {
      const result = await createMeetingShares(meetingId, {
        userIds: selectedIds,
      })
      if (result.shared.length === 0) {
        // Not counted as a use: nobody gained access, and the reader is told
        // so rather than shown a success (BRAI-677).
        setError(t.shareRecipientsAlreadyHaveAccess)
        return
      }
      logActivity('minutes.shared_with_members', { meetingId })
      setSharedRecipients(result.shared)
      const newDirectShares = selectedIds.filter(
        (id) => !users.find((u) => u.id === id)?.alreadyShared,
      ).length
      onShared?.(result.shared, { newDirectShares })
      await reloadShares()
      setSelectedIds([])
      setStep('success')
    } catch (err) {
      logActivity('minutes.shared_with_members', { meetingId, status: 'failure' })
      setError(err instanceof Error ? err.message : t.failedShareMeetingService)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleConfirmRevoke() {
    if (!revokeTarget) return
    setRevoking(true)
    const previousCurrent = currentShares
    const previousRemoved = removedShares
    // Optimistic: move to REMOVED
    setCurrentShares((c) =>
      c.filter((row) => row.user.id !== revokeTarget.user.id),
    )
    setRemovedShares((r) => [
      {
        user: revokeTarget.user,
        removedAt: Date.now(),
      },
      ...r,
    ])
    try {
      await revokeMeetingShare(meetingId, revokeTarget.user.id)
      onRevoked?.()
      setRevokeTarget(null)
      setStep('pick')
    } catch (err) {
      setCurrentShares(previousCurrent)
      setRemovedShares(previousRemoved)
      setError(err instanceof Error ? err.message : t.failedRevokeShare)
      setStep('pick')
    } finally {
      setRevoking(false)
    }
  }

  const dialogTitle =
    step === 'confirm'
      ? t.confirmShareTitle
      : step === 'success'
        ? t.meetingSharedTitle
        : step === 'revokeConfirm'
          ? fmt(t.revokeShareTitle, { name: revokeTarget?.user.name ?? '' })
          : t.shareMeetingTitle

  const successNames = formatSuccessNames(
    sharedRecipients,
    t.meetingSharedWithOthers,
  )

  const dateLocale = locale === 'ja' ? 'ja-JP' : 'en-US'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/30 px-3 py-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        aria-label={t.close}
        className="fixed inset-0 cursor-default"
        onClick={() => {
          if (!submitting && !revoking) onClose()
        }}
      />

      <div className="animate-scale-in relative flex w-full max-w-md flex-col rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-2xl">
        <div className="border-b border-[var(--border-color)] px-5 py-4 sm:px-6">
          <h2 id={titleId} className="text-lg font-bold text-[var(--text-primary)]">
            {dialogTitle}
          </h2>
          {step === 'confirm' && (
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {meetingTitle} · {t.minutesTranscriptAndAudio}
            </p>
          )}
          {step === 'pick' && (
            <p className="mt-1 truncate text-sm text-[var(--text-secondary)]">
              {meetingTitle}
            </p>
          )}
        </div>

        {step === 'pick' && (
          <>
            {!loadingShares && currentShares.length > 0 && (
              <div className="border-b border-[var(--border-color)] px-5 py-3 sm:px-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
                  {t.currentlySharedWith}
                </p>
                <ul className="mt-2 space-y-2">
                  {currentShares.map((row) => (
                    <li
                      key={row.user.id}
                      className="flex items-center gap-3"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent-light)] text-xs font-bold text-[var(--accent)]">
                        {initials(row.user.name)}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[var(--text-primary)]">
                        {row.user.name}
                      </span>
                      <button
                        type="button"
                        className="text-xs font-semibold text-red-500"
                        onClick={() => {
                          setRevokeTarget(row)
                          setStep('revokeConfirm')
                        }}
                      >
                        {t.revoke}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!loadingShares && removedShares.length > 0 && (
              <div className="border-b border-[var(--border-color)] px-5 py-3 sm:px-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
                  {t.removedSharesSection}
                </p>
                <ul className="mt-2 space-y-2 opacity-50">
                  {removedShares.map((row) => (
                    <li key={row.user.id} className="flex items-center gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--bg-tertiary)] text-xs font-bold text-[var(--text-tertiary)]">
                        {initials(row.user.name)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-[var(--text-secondary)]">
                          {row.user.name}
                        </span>
                        {row.removedAt != null && (
                          <span className="block text-xs text-[var(--text-tertiary)]">
                            {fmt(t.removedOnDate, {
                              date: new Date(row.removedAt).toLocaleDateString(
                                dateLocale,
                              ),
                            })}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="px-5 pt-4 sm:px-6">
              <label htmlFor={searchId} className="sr-only">
                {t.searchByNameOrEmail}
              </label>
              <input
                id={searchId}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.searchByNameOrEmail}
                className="input-field"
                autoComplete="off"
                disabled={loadingUsers && users.length === 0}
              />
            </div>

            <ul
              className="mt-3 max-h-64 overflow-y-auto px-2 sm:px-3"
              role="listbox"
              aria-multiselectable
            >
              {loadingUsers && users.length === 0 && (
                <li className="px-3 py-6 text-center text-sm text-[var(--text-secondary)]">
                  {t.loading}
                </li>
              )}
              {!loadingUsers && usersError && (
                <li className="px-3 py-6 text-center text-sm text-red-500">
                  {usersError}
                </li>
              )}
              {!loadingUsers && !usersError && users.length === 0 && (
                <li className="px-3 py-6 text-center text-sm text-[var(--text-secondary)]">
                  {t.shareDirectoryNoMatch}
                </li>
              )}
              {users.map((user) => {
                const checked = selectedIds.includes(user.id)
                return (
                  <li key={user.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={checked}
                      onClick={() => toggleUser(user)}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-[var(--bg-tertiary)]"
                    >
                      <span
                        className={[
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                          checked
                            ? 'border-[var(--accent)] bg-[var(--accent)] text-white'
                            : 'border-[var(--border-color)] bg-[var(--bg-primary)]',
                        ].join(' ')}
                        aria-hidden
                      >
                        {checked && (
                          <svg
                            viewBox="0 0 16 16"
                            className="h-3 w-3"
                            fill="currentColor"
                          >
                            <path d="M6.5 11.5 3 8l1-1 2.5 2.5L12 4l1 1z" />
                          </svg>
                        )}
                      </span>
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--accent-light)] text-xs font-bold text-[var(--accent)]">
                        {initials(user.name)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-[var(--text-primary)]">
                          {user.name}
                        </span>
                        <span className="block truncate text-xs text-[var(--text-secondary)]">
                          {user.email}
                        </span>
                      </span>
                      {user.alreadyShared && (
                        <span className="shrink-0 text-[10px] font-semibold uppercase text-[var(--text-tertiary)]">
                          {t.alreadySharedLabel}
                        </span>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>

            {error && step === 'pick' && (
              <p className="px-5 text-sm text-[var(--error)]" role="alert">
                {error}
              </p>
            )}

            <div className="flex items-center justify-between gap-3 border-t border-[var(--border-color)] px-5 py-4 sm:px-6">
              <p className="text-sm text-[var(--text-secondary)]">
                {fmt(t.nSelected, { count: selectedIds.length })}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {/* The way out of the whole flow, and it was missing. Every
                    other step leads back here — Back from `confirm`, Cancel
                    from `revokeConfirm` — so with nothing here, a reader who
                    opened Share by mistake had only the Escape key or a click
                    on the backdrop, which is an invisible button. */}
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-[var(--border-color)] px-4 py-2 text-sm font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)]"
                >
                  {t.cancel}
                </button>
                <button
                  type="button"
                  disabled={selectedIds.length === 0}
                  onClick={() => {
                    setError(null)
                    setStep('confirm')
                  }}
                  className="btn-primary px-4 py-2 disabled:pointer-events-none disabled:opacity-50"
                >
                  {t.continueAction}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <div className="px-5 py-4 sm:px-6">
            <div className="flex flex-wrap gap-2">
              {selectedUsers.map((user) => (
                <span
                  key={user.id}
                  className="rounded-full bg-[var(--accent-light)] px-3 py-1 text-sm font-medium text-[var(--accent)]"
                >
                  {user.name}
                </span>
              ))}
            </div>
            <p className="mt-4 text-sm text-[var(--text-secondary)]">
              {t.recipientsCanViewDisclaimer}
            </p>
            {error && (
              <p className="mt-3 text-sm text-[var(--error)]" role="alert">
                {error}
              </p>
            )}
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                disabled={submitting}
                onClick={() => setStep('pick')}
                className="rounded-lg border border-[var(--border-color)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
              >
                {t.back}
              </button>
              <button
                type="button"
                disabled={submitting || selectedIds.length === 0}
                onClick={() => void handleConfirmShare()}
                className="btn-primary px-4 py-2 disabled:pointer-events-none disabled:opacity-50"
              >
                {submitting ? t.loading : t.share}
              </button>
            </div>
          </div>
        )}

        {step === 'revokeConfirm' && revokeTarget && (
          <div className="px-5 py-4 sm:px-6">
            <p className="text-sm text-[var(--text-secondary)]">
              {t.revokeShareBody}
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                disabled={revoking}
                onClick={() => {
                  setRevokeTarget(null)
                  setStep('pick')
                }}
                className="rounded-lg border border-[var(--border-color)] px-4 py-2 text-sm font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
              >
                {t.cancel}
              </button>
              <button
                type="button"
                disabled={revoking}
                onClick={() => void handleConfirmRevoke()}
                className="rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-600 disabled:opacity-50"
              >
                {revoking ? t.loading : t.removeAccess}
              </button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="px-5 py-5 sm:px-6">
            <div
              className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
              aria-hidden
            >
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-sm leading-6 text-[var(--text-secondary)]">
              {fmt(t.meetingSharedBody, {
                meetingName: meetingTitle,
                names: successNames,
              })}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="btn-primary mt-6 w-full px-4 py-2.5"
            >
              {t.done}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
